import { useMutation } from '@apollo/react-hooks';
import {
  Button,
  createStyles,
  IconButton,
  makeStyles,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import { Link } from '@reach/router';
import moment from 'moment';
import React, { useState } from 'react';
import {
  cashRegisterDifferenceResolved,
  cashRegisterSetDifferenceComment,
} from '../../graphql/queries';
import {
  CashRegisterDifferenceResolved,
  CashRegisterDifferenceResolvedVariables,
  GetCashRegisterRecords,
} from '../../graphql/types';
import { formatTime } from '../../lib/utils';

import { Divider } from '@material-ui/core';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';

interface Props {
  data: GetCashRegisterRecords;
}

const mapDay: any = {
  0: 'Neděle',
  1: 'Pondělí',
  2: 'Úterý',
  3: 'Středa',
  4: 'Čtvrtek',
  5: 'Pátek',
  6: 'Sobota',
};

const RecordsList = ({ data }: Props) => {
  const classes = useStyles();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [comment, setComment] = useState('');

  const [resolveDifferent] = useMutation<
    CashRegisterDifferenceResolved,
    CashRegisterDifferenceResolvedVariables
  >(cashRegisterDifferenceResolved);

  const [setDifferenceComment] = useMutation(cashRegisterSetDifferenceComment);

  const handleResolveDifference = (id: string | null) => {
    if (id) {
      resolveDifferent({ variables: { id } });
    }
  };

  const handleSaveComment = (id: string | null) => {
    if (id && comment.trim()) {
      setDifferenceComment({ variables: { id, comment: comment.trim() } });
      setEditingId(null);
      setComment('');
    }
  };

  const handleOpenComment = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    e.preventDefault();
    setComment('');
    setEditingId(id);
  };

  const handleCancelComment = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setEditingId(null);
    setComment('');
  };

  return (
    <div>
      <List>
        <ListItem>
          <ListItemText
            className={`${classes.cell} ${classes.day}`}
            style={{ width: '15%' }}
          >
            Den
          </ListItemText>
          <ListItemText
            className={`${classes.cell} ${classes.createdAt}`}
            style={{ width: '30%' }}
          >
            Kasa otevřena
          </ListItemText>
          <ListItemText
            className={`${classes.cell} ${classes.closedAt}`}
            style={{ width: '30%' }}
          >
            Kasa zavřena
          </ListItemText>
          <ListItemText
            className={`${classes.cell} ${classes.closedWithDiff}`}
            style={{ width: '30%' }}
          >
            Zavřeno s rozdílem
          </ListItemText>
          <ListItemText
            className={`${classes.cell} ${classes.diffResolved}`}
            style={{ width: '15%' }}
          >
            Rozdíl vyřešen
          </ListItemText>
          <ListItemText
            className={`${classes.cell} ${classes.commentCol}`}
            style={{ width: '20%' }}
          >
            Komentář
          </ListItemText>
        </ListItem>
      </List>
      <Divider />
      <List>
        {data?.cashRegisterRecords.map(item => (
          <ListItem key={item.id} alignItems="flex-start">
            <ListItemLink
              className={`${classes.cell} ${classes.day}`}
              style={{ width: '15%' }}
              itemId={item.id}
            >
              {mapDay[moment(item.openedAt).day()]}
            </ListItemLink>
            <ListItemLink
              className={`${classes.cell} ${classes.createdAt}`}
              style={{ width: '30%' }}
              itemId={item.id}
            >
              {formatTime(item.openedAt)}
            </ListItemLink>
            <ListItemLink
              className={`${classes.cell} ${classes.closedAt}`}
              style={{ width: '30%' }}
              itemId={item.id}
            >
              {item.closedAt ? formatTime(item.closedAt) : '-'}
            </ListItemLink>
            <ListItemLink
              className={`${classes.cell} ${classes.closedWithDiff}`}
              style={{ width: '30%' }}
              itemId={item.id}
            >
              {item.closedWithDifference
                ? item.closedDifference
                : item.closedWithDifference == null
                ? '-'
                : 'Ne'}
            </ListItemLink>

            {/* Rozdíl vyřešen column */}
            {item.closedWithDifference && !item.differenceResolved ? (
              <ListItem
                className={`${classes.cell} ${classes.diffResolved}`}
                style={{ width: '15%' }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleResolveDifference(item.closedId);
                  }}
                >
                  Vyřešit
                </Button>
              </ListItem>
            ) : (
              <ListItemLink
                className={`${classes.cell} ${classes.diffResolved}`}
                style={{ width: '15%' }}
                itemId={item.id}
              >
                {item.differenceResolved ? 'Ano' : '-'}
              </ListItemLink>
            )}

            {/* Comment column — available on every row */}
            <ListItem
              className={`${classes.cell} ${classes.commentCol}`}
              style={{ width: '20%', padding: 0 }}
            >
              <div className={classes.commentWrapper}>
                {/* Row: text + pencil */}
                <div className={classes.commentHeader}>
                  {item.differenceComment ? (
                    <Tooltip title={item.differenceComment} placement="top">
                      <Typography
                        variant="body2"
                        className={classes.commentText}
                        noWrap
                      >
                        {item.differenceComment}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <span className={classes.commentEmpty} />
                  )}
                  <IconButton
                    size="small"
                    title="Přidat komentář"
                    className={classes.commentButton}
                    onClick={e => handleOpenComment(e, item.id)}
                  >
                    <EditIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </div>

                {/* Inline input — expands below when open */}
                {editingId === item.id && (
                  <div
                    className={classes.commentRow}
                    onClick={e => e.stopPropagation()}
                  >
                    <TextField
                      autoFocus
                      size="small"
                      variant="outlined"
                      placeholder="Komentář..."
                      value={comment}
                      onChange={e => setComment(e.target.value)}
                      inputProps={{
                        style: { padding: '4px 6px', fontSize: 12 },
                      }}
                      style={{ flex: 1 }}
                    />
                    <IconButton
                      size="small"
                      color="primary"
                      disabled={!comment.trim()}
                      onClick={e => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleSaveComment(item.id);
                      }}
                    >
                      <CheckIcon style={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancelComment}>
                      <CloseIcon style={{ fontSize: 16 }} />
                    </IconButton>
                  </div>
                )}
              </div>
            </ListItem>
          </ListItem>
        ))}
      </List>
    </div>
  );
};

interface ItemProps {
  itemId: string;
  style: React.CSSProperties;
  children: React.ReactNode;
  className: string;
}

const ListItemLink = ({ children, itemId, ...props }: ItemProps) => (
  <ListItem
    {...props}
    component={(props: any) => (
      <Link to={`/cash-register/${itemId}`} {...props} />
    )}
  >
    {children}
  </ListItem>
);

export default RecordsList;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    cell: {
      padding: 0,
    },
    day: {
      width: '15%',
    },
    createdAt: {
      width: '30%',
    },
    closedAt: {
      width: '30%',
    },
    closedWithDiff: {
      width: '30%',
    },
    diffResolved: {
      width: '15%',
    },
    commentCol: {
      width: '20%',
    },
    commentWrapper: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    commentHeader: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      minHeight: 36,
    },
    commentText: {
      flex: 1,
      fontSize: 13,
      color: theme.palette.text.primary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      paddingRight: 4,
    },
    commentEmpty: {
      flex: 1,
    },
    commentButton: {
      flexShrink: 0,
      padding: 4,
      color: theme.palette.text.secondary,
      '&:hover': {
        color: theme.palette.primary.main,
      },
    },
    commentRow: {
      display: 'flex',
      alignItems: 'center',
      marginTop: 4,
      gap: 2,
      width: '100%',
    },
    paper: {
      padding: 16,
      display: 'flex',
      justifyContent: 'space-around',
      flexFlow: 'wrap',
    },
    customWidth: {
      maxWidth: 190,
    },
    datePicker: {
      marginLeft: 64,
      marginRight: 32,
    },
    right: {
      paddingRight: 15,
    },
    tableCellHead: {
      color: 'rgba(0, 0, 0, 0.54)',
      fontSize: '0.75rem',
      padding: '4px 24px 4px 24px;',
    },
    tableCell: {
      color: 'rgba(0, 0, 0, 0.87)',
      fontSize: '14px',
      padding: '8px 24px 8px 24px',
    },
  }),
);
